import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { Button, Dropdown } from "react-bootstrap";
import * as Yup from "yup";

import PubSub from "../../services/PubSub";
import User from "../../services/user";
import Domain from "../../services/domain";
import Toolbar from "../controls/Toolbar";
import TextBox from "../controls/form/TextBox";
import DynamicRender from "../controls/DynamicRender";
import ConfirmationPrompt from "../controls/ConfirmationPrompt";
import Utils from "../../utils/utils";
import { useGetUser, useHasPermission, usePermissionsInDomain } from "../../common/Hooks";
import AssignedPermissions from "./AssignedPermissions";
import Toggle from "../controls/Toggle";
import GenericDropdown from "../controls/GenericDropdown";
import GenericTextBox from "../controls/GenericTextBox";
import GenericButtonGroup from "../controls/GenericButtonGroup";
import RolesMapping, { getRoleAssignedToUser } from "./permissions/rolesMapping";
import ActionButton from "../settings/ActionButton";
import Label from "../controls/form/Label";

const UserDetails = ({ userIdLoc = 'id', deploymentIdLoc, showRolesOnly = false } = {}) => {
    const [translate] = useTranslation();
    const [enabled, setEnabled] = useState(false);
    const [isGuest, setIsGuest] = useState(false);
    const [appAdmin, setAppAdmin] = useState(false);
    const [assignedPermissions, setAssignedPermissions] = useState([]);
    const [render, setRender] = useState(false);
    const [user, setUser] = useState({});
    const [crossUser, setCrossUser] = useState(false);
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showSavePermissionsButton, setShowSavePermissionsButton] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [allDomains, setAllDomains] = useState([]);
    const [assignedDomains, setAssignedDomains] = useState([]);
    const [assignedAdminDomains, setAssignedAdminDomains] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState(Utils.getDomain());
    const [assignedRole, setAssignedRole] = useState();
    const [me] = useGetUser();
    const allPermissions = usePermissionsInDomain({ domainId: selectedDomain });

    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: null,
        type: "user"
    });

    const { id: paramsId, objectSubTypeId } = useParams();
    const [isRootDomain] = useState(() => Utils.isRootDomain());
    const [{ _id: myUserId }] = useGetUser();
    const userId = userIdLoc === 'id' ? paramsId : objectSubTypeId;
    const deploymentId = deploymentIdLoc === 'id' ? paramsId : null;

    const getUserDetails = useCallback(async (id) => {
        const response = await User.getUser(id);
        const user = response.data;
        if (user) {
            setUser(user);
            setAppAdmin(user.appAdmin);
            setEnabled(user.enabled);
            setIsGuest(user.isGuest);
            setRender(true);
            setShowSaveButton(false);
            setShowSavePermissionsButton(false);
            setCrossUser(!user.singleDomainUser);
            if (user.singleDomainUser) {
                setSelectedDomain(user.domain[0]._id);
            }
        }
    }, []);

    useEffect(() => {
        if (user.domain) {
            setAssignedDomains(user.domain.map(domain => domain._id));
        }
        if (user.adminForDomains) {
            setAssignedAdminDomains(user.adminForDomains);
        }
    }, [user.domain, user.adminForDomains]);

    useEffect(() => {
        if (user.uiPermissions) {
            setAssignedPermissions(user.uiPermissions.map(permission => permission._id));
        }
    }, [user.uiPermissions]);

    const getAllDomains = useCallback(async () => {
        const response = await Domain.getAllDomains();
        if (!response.error) {
            setAllDomains(response.data);
        }
    }, []);

    useEffect(() => {
        if (userId) {
            getUserDetails(userId);
        }
        const subscribeId = PubSub.subscribe("refresh-user-details", () => {
            getUserDetails(userId);
        });

        return () => PubSub.unsubscribe("refresh-user-details", subscribeId);
    }, [userId, getUserDetails]);

    useEffect(() => {
        if (isRootDomain) {
            getAllDomains();
        }
    }, [isRootDomain, getAllDomains]);

    useEffect(() => {
        if (showSaveButton) {
            PubSub.publish("has-unsaved-changes");
        } else {
            PubSub.publish("no-unsaved-changes");
        }
    }, [showSaveButton]);

    useEffect(() => {
        const role = getRoleAssignedToUser({
            userPermissions: user.uiPermissions,
            allPermissions,
            selectedDomain,
            deploymentId
        });

        setAssignedRole(role?._id);
    }, [user.uiPermissions, allPermissions, selectedDomain, deploymentId]);

    const updateUser = useCallback(async () => {
        const updatedObj = {
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            appAdmin,
            enabled,
            isGuest,
            uiPermissions: assignedPermissions,
            adminForDomains: assignedAdminDomains,
            singleDomainUser: !crossUser
        };

        if (crossUser && assignedDomains.length > 0) {
            updatedObj.domain = assignedDomains;
        }

        if (!crossUser && selectedDomain) {
            updatedObj.domain = [selectedDomain];
        }

        const response = await User.updateUser(userId, updatedObj);
        if (!response.error) {
            PubSub.publish("refresh-user-details");
        }
    }, [userId, user, assignedDomains, assignedAdminDomains, appAdmin, isGuest, enabled, assignedPermissions, crossUser, selectedDomain]);

    const deleteUser = useCallback(async () => {
        const response = await User.deleteUser(userId);
        if (!response.error) {
            setRender(false);
            setShowConfirmation(false);
            PubSub.publish("refresh-user-list", { userDeleted: true });
        }
    }, [userId, setRender, setShowConfirmation]);

    const isChecked = useCallback((domain) => {
        return assignedDomains.indexOf(domain._id) > -1;
    }, [assignedDomains]);

    const isAdminChecked = useCallback((domain) => {
        return assignedAdminDomains.indexOf(domain._id) > -1;
    }, [assignedAdminDomains]);

    const updateAssignedDomains = useCallback((domain) => {
        setShowSavePermissionsButton(true);
        if (assignedDomains.indexOf(domain._id) > -1) {
            const domains = [...assignedDomains];
            domains.splice(assignedDomains.indexOf(domain._id), 1);
            setAssignedDomains(domains);
        } else {
            setAssignedDomains([...assignedDomains, domain._id]);
        }
    }, [assignedDomains, setAssignedDomains]);

    const updateAssignedAdminDomains = useCallback((domain) => {
        setShowSavePermissionsButton(true);
        if (assignedAdminDomains.indexOf(domain._id) > -1) {
            const domains = [...assignedAdminDomains];
            domains.splice(assignedAdminDomains.indexOf(domain._id), 1);
            setAssignedAdminDomains(domains);
        } else {
            setAssignedAdminDomains([...assignedAdminDomains, domain._id]);
        }
    }, [assignedAdminDomains, setAssignedAdminDomains]);

    const updateAssignedPermissions = useCallback((permissions, reset = false) => {
        if (showRolesOnly) {
            setShowSaveButton(true);
        }
        else {
            setShowSavePermissionsButton(true);
        }

        const assignedPermissionsOnPage = Object.keys(permissions).filter(permissionId => permissions[permissionId].assigned === true);
        const unassignedPermissionsOnPage = Object.keys(permissions).filter(permissionId => permissions[permissionId].assigned === false);

        setAssignedPermissions(currentPermissions => {
            const addedPermissions = [];
            const allPermissions = reset ? [] : [...currentPermissions];
            for (const permission of assignedPermissionsOnPage) {
                if (!allPermissions.includes(permission)) {
                    addedPermissions.push(permission);
                }
            }
            for (const permission of unassignedPermissionsOnPage) {
                if (allPermissions.includes(permission)) {
                    allPermissions.splice(allPermissions.indexOf(permission), 1);
                }
            }
            return [...allPermissions, ...addedPermissions];
        });
    }, [showRolesOnly]);

    const renderToolbar = useMemo(() => {
        const buttons = [
            {
                name: "Save",
                title: translate("OTM_SAVE"),
                onClick: updateUser,
                hidden: !showSaveButton
            }
        ];

        if (myUserId !== userId && permissions.delete) {
            buttons.push({
                name: "DeleteOutlined",
                title: translate("OTM_DELETE"),
                onClick: () => setShowConfirmation(true)
            });
        }

        return (
            <Toolbar
                buttons={buttons}
                heading={translate("OTM_USER_DETAILS")}
            />
        );
    }, [translate, updateUser, showSaveButton, myUserId, userId, permissions.delete]);

    const getPermissionsFromSelectedList = useCallback((uiPermissionsList) => {
        const foundPermissions = [];
        uiPermissionsList.forEach((permissionName) => {
            foundPermissions.push(...Object.values(allPermissions).filter(({ name, domain, deployment }) => {
                // adding for the current deployment
                return name === permissionName && domain._id === selectedDomain && (!deploymentId || deployment === null || deployment._id === deploymentId);
            }));
        });

        const result = {};
        foundPermissions.forEach(permission => {
            result[permission._id] = {
                ...permission,
                assigned: true,
            };
        });

        updateAssignedPermissions(result, true);
    }, [selectedDomain, deploymentId, allPermissions, updateAssignedPermissions]);

    const renderDomainSelector = useMemo(() => {
        if (appAdmin || !isRootDomain) {
            return <></>
        }

        return <div className="col-6 mt-3">
            <label className="d-block form-label h6">{translate("OTM_DOMAIN")}</label>
            {
                crossUser ? <GenericDropdown
                    options={allDomains}
                    valueKey='_id'
                    displayValueKey='name'
                    onSelect={(id) => setSelectedDomain(id)}
                    defaultValue={allDomains.find(d => d._id.toString() === selectedDomain)?.name}
                    grow={true} />
                    : <GenericTextBox disabled={true} defaultValue={allDomains.find(d => d._id.toString() === selectedDomain)?.name} />
            }
        </div>
    }, [appAdmin, translate, selectedDomain, allDomains, isRootDomain, crossUser]);

    const renderPredefinedRolesSelector = useMemo(() => {
        return <div className="col-6 mt-3">
            <Dropdown className={`${isRootDomain ? 'mt-4' : ''}`} >
                <Dropdown.Toggle variant="primary" className={`${assignedAdminDomains.includes(selectedDomain) || !permissions.update ? 'btn-disabled' : ''}`} >
                    {translate("OTM_AUTOFILL_PREDEFINED_ROLES")}
                </Dropdown.Toggle>
                <Dropdown.Menu >
                    {RolesMapping.map(role => <Dropdown.Item
                        onClick={() => { getPermissionsFromSelectedList(role.uiPermissionsList) }}
                        key={role._id}>
                        {role.name}
                    </Dropdown.Item>)}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    }, [isRootDomain, assignedAdminDomains, selectedDomain, getPermissionsFromSelectedList, permissions, translate]);

    const renderAssignedPermissions = useMemo(() =>
        !appAdmin && assignedAdminDomains && !assignedAdminDomains.includes(selectedDomain) && (
            <div id="assignedPermissions" className="col-12 mt-3">
                <AssignedPermissions
                    domain={selectedDomain}
                    onChange={updateAssignedPermissions}
                    userPermissions={permissions}
                    updatedAssignedPermissions={assignedPermissions}
                    allPermissions={allPermissions}
                />
            </div>
        ), [appAdmin, assignedAdminDomains, selectedDomain, permissions, assignedPermissions, updateAssignedPermissions, allPermissions]);

    const renderDomainAccess = useMemo(() => {
        if (!isRootDomain) {
            return <></>
        }

        return <div className="col-12 col-lg-6 mt-3" key={"access" + selectedDomain}>
            <label htmlFor={selectedDomain} className="d-block form-label h6 mb-0">{translate("OTM_DOMAIN_ACCESS")}</label>
            <div id="domainAccess">
                <div className="form-check form-switch">
                    <Toggle
                        id={selectedDomain}
                        disabled={user.appAdmin || !permissions.update || !crossUser}
                        defaultState={isChecked({ _id: selectedDomain })}
                        onChange={() => updateAssignedDomains({ _id: selectedDomain })}
                    />
                </div>
            </div>
        </div>
    }, [crossUser, isChecked, isRootDomain, selectedDomain, translate, permissions.update, updateAssignedDomains, user.appAdmin])

    const renderPermissionsCard = useMemo(() => {
        if (appAdmin) {
            return <></>
        }
        return (
            <div className="card mt-3">
                <div className="card-header border-bottom">
                    <Toolbar
                        buttons={[
                            {
                                name: "Save",
                                title: translate("OTM_SAVE"),
                                onClick: updateUser,
                                hidden: !showSavePermissionsButton
                            }
                        ]}
                        heading={translate("OTM_PERMISSIONS")}
                    />
                </div>
                <div className="card-body">
                    <div className="row mt-n3" id="availableDomains">
                        {renderDomainSelector}
                        {renderPredefinedRolesSelector}
                        {renderDomainAccess}
                        <div className="col-12 col-lg-6 mt-3" key={"admin" + selectedDomain}>
                            <label htmlFor={selectedDomain} className="d-block form-label h6 mb-0">{translate("OTM_ADMIN")}</label>
                            <div id="appAdmin">
                                <div className="form-check form-switch">
                                    <Toggle
                                        id={selectedDomain}
                                        disabled={user.appAdmin || !permissions.update}
                                        defaultState={isAdminChecked({ _id: selectedDomain })}
                                        onChange={() => updateAssignedAdminDomains({ _id: selectedDomain })}
                                    />
                                </div>
                            </div>
                        </div>
                        {renderAssignedPermissions}
                    </div>
                </div>
            </div>
        );
    }, [user.appAdmin, appAdmin, selectedDomain, translate, isAdminChecked, updateAssignedAdminDomains, permissions.update, renderPredefinedRolesSelector,
        renderDomainSelector, renderAssignedPermissions, renderDomainAccess, updateUser, showSavePermissionsButton]);

    const resendVerification = useMemo(() => user.isVerified ? null : (
        <div className="col-12 mt-3">
            <Button
                variant="primary"
                disabled={!permissions.update}
                onClick={async () => {
                    await User.resendVerification(user._id);
                }}
            >
                {translate("OTM_USER_RESEND_VERIFICATION")}
            </Button>
        </div>
    ), [translate, user.isVerified, user._id, permissions.update]);

    const appAdminSelector = useMemo(() => {
        if (user.appAdmin || crossUser) {
            return (
                <div className="col-12 col-sm-6 col-lg-3 mt-3">
                    <label htmlFor="appAdmin" className="d-block form-label h6 mb-0">{translate("OTM_APPLICATION_ADMIN_?")}</label>
                    <div id="appAdmin">
                        <div className="form-check form-switch">
                            <Toggle
                                defaultState={appAdmin}
                                onChange={() => { setAppAdmin(!appAdmin); setShowSaveButton(true) }}
                                disabled={myUserId === userId || !permissions.update}
                            />
                        </div>
                    </div>
                </div>
            );
        }
        else return null;
    }, [user.appAdmin, crossUser, translate, appAdmin, myUserId, userId, permissions.update]);

    const rolesSelector = useMemo(() => {
        if (user.appAdmin) {
            return null;
        }

        return (
            <div className="col-12 mt-3">
                <GenericButtonGroup
                    heading={'OTM_USERS_ROLE'}
                    options={RolesMapping}
                    defaultValue={assignedRole}
                    valueKey='_id'
                    displayValueKey='name'
                    onSelect={(selectedRoleId) => {
                        getPermissionsFromSelectedList(RolesMapping.find(({ _id }) => _id === selectedRoleId)?.uiPermissionsList || []);
                    }}
                    disabled={myUserId === userId || !permissions.update}
                    key={userId}
                />
            </div>
        );
    }, [user.appAdmin, myUserId, userId, permissions.update, assignedRole, getPermissionsFromSelectedList]);

    const userEnabledSelector = useMemo(() => (
        <div className="col-12 col-sm-6 col-lg-3 mt-3">
            <label htmlFor="userEnabled" className="d-block form-label h6 mb-0">{translate("OTM_USER_ENABLED_?")}</label>
            <div id="userEnabled">
                <div className="form-check form-switch">
                    <Toggle
                        defaultState={enabled}
                        disabled={myUserId === userId || !permissions.update}
                        onChange={() => { setEnabled(!enabled); setShowSaveButton(true) }}
                    />
                </div>
            </div>
        </div>
    ), [translate, enabled, myUserId, userId, permissions.update]);

    const userIsGuestSelector = useMemo(() => me.appAdmin ? (
        <div className="col-12 col-sm-6 col-lg-3 mt-3">
            <label htmlFor="isGuest" className="d-block form-label h6 mb-0">{translate("OTM_GUEST_USER?")}</label>
            <div id="isGuest">
                <div className="form-check form-switch">
                    <Toggle
                        defaultState={isGuest}
                        disabled={myUserId === userId || !permissions.update}
                        onChange={() => { setIsGuest(!isGuest); setShowSaveButton(true) }}
                    />
                </div>
            </div>
        </div>
    ) : null, [translate, isGuest, myUserId, userId, permissions.update, me.appAdmin]);

    const crossDomainUserSelector = useMemo(() => {
        if (!Utils.isRootDomain() || appAdmin) {
            return <></>
        }

        return (
            <div className="col-12 col-sm-6 col-lg-3 mt-3">
                <label htmlFor="userCross" className="d-block form-label h6 mb-0">{translate("OTM_CROSS_DOMAIN_USER_?")}</label>
                <div id="userCross">
                    <div className="form-check form-switch">
                        <Toggle
                            defaultState={crossUser}
                            disabled={myUserId === userId || !permissions.update}
                            onChange={() => { setCrossUser(!crossUser); setShowSaveButton(true) }}
                        />
                    </div>
                </div>
            </div>
        );
    }, [translate, crossUser, myUserId, userId, permissions.update, appAdmin]);

    return (
        <DynamicRender render={render} style={{ overflowX: 'hidden', overflowY: 'auto' }}>
            <ConfirmationPrompt show={showConfirmation} message={translate("OTM_DELETE_USER_?")}
                onConfirm={deleteUser} onCancel={() => setShowConfirmation(false)} />
            <Formik initialValues={{
                firstName: user.firstName || "",
                lastName: user.lastName || "",
                email: user.email || "",
                domain: (user.domain && user.domain[0]?.name) || "",
                userId: user._id || ""
            }} validationSchema={Yup.object({
                email: Yup.string().email(translate("OTM_INVALID_EMAIL")).required(" ")
            })} enableReinitialize>
                <Form onChange={(event) => {
                    if (event.target.name && event.target.name !== "password" && event.target.name !== "confirmPassword") {
                        setUser({ ...user, [event.target.name]: event.target.value });
                        setShowSaveButton(true);
                    }
                }}>
                    <div className="card h-100 mt-3">
                        <div className="card-header border-bottom">
                            {renderToolbar}
                        </div>
                        <div className="card-body">
                            <div className="row mt-n3 align-items-end">
                                <TextBox css="col-12 col-lg-6 mt-3" label={translate("OTM_FIRST_NAME")} type="text" name="firstName" disabled={!permissions.update} />
                                <TextBox css="col-12 col-lg-6 mt-3" label={translate("OTM_LAST_NAME")} type="text" name="lastName" disabled={!permissions.update} />
                                <TextBox css="col-12 col-lg-6 mt-3" label={translate("OTM_EMAIL")} type="email" name="email" disabled={true} />
                                <div className="col-12 col-lg-6 mt-3">
                                    <Label
                                        controlId={"userId"}
                                        label={translate("OTM_USER_ID")}
                                    />
                                    <div className="row gx-2 align-items-center">
                                        <div className="col">
                                            <TextBox css="" type="text" name="userId" disabled={true} />
                                        </div>
                                        <div className="col-auto">
                                            <ActionButton actionType="copy" valueToCopy={user._id} />
                                        </div>
                                    </div>
                                </div>
                                {resendVerification}
                                {userEnabledSelector}
                                {userIsGuestSelector}
                                {crossDomainUserSelector}
                                {appAdminSelector}
                                {showRolesOnly ? rolesSelector : null}
                            </div>
                        </div>
                    </div>
                    {showRolesOnly ? null : renderPermissionsCard}
                </Form>
            </Formik>
        </DynamicRender>
    );
};

export default UserDetails;
